import moment from 'moment';
import theme from '../../ordersTheme.module.scss';

/**
 * Table cell primitives for the orders queue.
 *
 * Colour used to come from antd's tag palette — nine hues across status,
 * instrument, order type and retry, none of them related to each other or to
 * the brand. Here colour means one thing only: how an order is doing. Every
 * other categorical value renders in the neutral pill, because the label
 * already says what it is.
 */

const TONE = {
  ok: theme.toneOk,
  warn: theme.toneWarn,
  stop: theme.toneStop,
  info: theme.toneInfo,
  neutral: theme.toneNeutral,
  brand: theme.toneBrand,
};

/** Same severity each status carried before, in the brand palette. */
const STATUS_TONE = {
  FILLED: 'ok',
  SETTLED: 'info',
  OPEN: 'brand',
  PENDING: 'warn',
  PARTIALLY_FILLED: 'warn',
  ERROR: 'stop',
  REJECTED: 'stop',
  CANCELLED: 'stop',
  EXPIRED: 'stop',
};

export const Pill = ({tone = 'neutral', dot = false, children}) => (
  <span
    className={`${theme.pill} ${TONE[tone] ?? theme.toneNeutral} ${
      dot ? theme.pillDot : ''
    }`}>
    {children}
  </span>
);

export const Dash = () => <span className={theme.muted}>—</span>;

export const StatusPill = ({status}) =>
  status ? (
    <Pill dot tone={STATUS_TONE[status] ?? 'info'}>
      {String(status).replace(/_/g, ' ')}
    </Pill>
  ) : (
    <Dash />
  );

/**
 * A number, or an em dash. `toLocaleString()` on `undefined` throws and on a
 * failed multiplication yields the string "NaN", which is how the order total
 * column came to print NaN for an order with neither fill nor limit price.
 */
export const Num = ({value, decimals}) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return <Dash />;
  }
  const n = Number(value);
  return (
    <span className={theme.tabular}>
      {decimals === undefined
        ? n.toLocaleString()
        : n.toLocaleString(undefined, {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
          })}
    </span>
  );
};

export const DateCell = ({value, format = 'DD MMM YYYY, h:mmA'}) =>
  value ? (
    <span className={theme.tabular}>{moment(value).format(format)}</span>
  ) : (
    <Dash />
  );

export const Direction = ({value}) => {
  if (!value) return <Dash />;
  return (
    <span className={value === 'BUY' ? theme.buy : theme.sell}>{value}</span>
  );
};

export default theme;
